import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { format, parseISO } from "date-fns";

import * as styles from "./styles";
import AgentChangeGlow from "components/composerv2/components/AgentChangeGlow";
import type { ComposerAgentProfile } from "services/composerAgent";
import { logoImageSrc } from "containers/content-factory/visualProfile";

const MENU_GAP_PX = 6;
const MENU_ITEM_SELECTOR = "[role='menuitemradio']";
const CREATED_DATE_FORMAT = "d MMM yyyy";

interface MenuPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

export const profileLabel = (profile: ComposerAgentProfile): string => profile.name ?? "Untitled profile";

// Copies of one brand share a website, with or without www, or a name; the scheduler's prompt treats them the same way.
const brandKeys = (profile: ComposerAgentProfile): string[] =>
  [profile.website, profile.name].flatMap(key => (key ? [key.trim().toLowerCase().replace(/^www\./, "")] : []));

const hasCopies = (profile: ComposerAgentProfile, profiles: ComposerAgentProfile[]): boolean => {
  const keys = brandKeys(profile);
  return profiles.some(other => other.id !== profile.id && brandKeys(other).some(key => keys.includes(key)));
};

// Unnamed profiles are already called after their website, so it is not repeated; copies of one brand differ only by date.
const profileDetail = (profile: ComposerAgentProfile, profiles: ComposerAgentProfile[]): string =>
  [
    profile.website && profile.website !== profile.name ? profile.website : null,
    profile.language,
    hasCopies(profile, profiles) ? `created ${format(parseISO(profile.createdAt), CREATED_DATE_FORMAT)}` : null
  ]
    .filter(Boolean)
    .join(" · ");

// The menu lives in a portal with fixed coordinates, because the chat thread scrolls and would clip it.
const positionFor = (trigger: DOMRect, opensUp: boolean): MenuPosition =>
  opensUp
    ? { bottom: window.innerHeight - trigger.top + MENU_GAP_PX, right: window.innerWidth - trigger.right }
    : { top: trigger.bottom + MENU_GAP_PX, left: trigger.left };

const menuItems = (menu: HTMLElement | null): HTMLButtonElement[] =>
  Array.from(menu?.querySelectorAll<HTMLButtonElement>(MENU_ITEM_SELECTOR) ?? []);

export const ProfileLogo: React.FC<{ profile: ComposerAgentProfile; size: number }> = ({ profile, size }) => {
  const [failed, setFailed] = useState(false);
  const src = failed ? undefined : logoImageSrc(profile.logo);
  return (
    <styles.ProfileLogo $size={size} $hasImage={!!src} aria-hidden="true">
      {src ? <img src={src} alt="" onError={() => setFailed(true)} /> : profileLabel(profile).trim().charAt(0).toUpperCase()}
    </styles.ProfileLogo>
  );
};

interface ProfileMenuProps {
  profiles: ComposerAgentProfile[];
  activeId: string | null;
  onPick: (profileId: string) => void;
  variant: "inline" | "compact"; // inline sits in a sentence, compact in the input bar
  opensUp?: boolean; // the input bar is at the bottom of the panel, so its menu opens upward and aligns right
  disabled?: boolean;
  touchedAt?: number | null; // when Create AI last switched the profile, so the trigger glows
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ profiles, activeId, onPick, variant, opensUp = false, disabled = false, touchedAt = null }) => {
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const active = profiles.find(profile => profile.id === activeId);
  const isOpen = position !== null;
  const hasChoice = profiles.length > 1;

  const close = useCallback((restoreFocus: boolean) => {
    setPosition(null);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (disabled) setPosition(null);
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) return;
    const items = menuItems(menuRef.current);
    (items.find(item => item.getAttribute("aria-checked") === "true") ?? items[0])?.focus();

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) close(false);
    };
    // Captured and stopped, so Escape closes the menu without also closing the composer modal.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      close(true);
    };
    const onScroll = (event: Event) => {
      if (!menuRef.current?.contains(event.target as Node)) close(false);
    };
    const onResize = () => close(false);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [isOpen, close]);

  if (!active) return null;

  const toggle = () => {
    if (isOpen) close(false);
    else if (triggerRef.current) setPosition(positionFor(triggerRef.current.getBoundingClientRect(), opensUp));
  };

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = menuItems(menuRef.current);
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    items[(current + step + items.length) % items.length]?.focus();
  };

  return (
    <styles.ProfileTriggerWrap>
      <AgentChangeGlow touchedAt={touchedAt} radius={999} />
      <styles.ProfileTrigger
        ref={triggerRef}
        type="button"
        $variant={variant}
        $static={!hasChoice}
        disabled={disabled || !hasChoice}
        aria-haspopup={hasChoice ? "menu" : undefined}
        aria-expanded={hasChoice ? isOpen : undefined}
        title={hasChoice ? "Choose the Willow Create profile the chat writes with" : "The Willow Create profile the chat writes with"}
        onClick={toggle}
      >
        <ProfileLogo profile={active} size={variant === "inline" ? 18 : 16} />
        <span className="name">{profileLabel(active)}</span>
        {hasChoice && <styles.ProfileChevron $open={isOpen} />}
      </styles.ProfileTrigger>
      {isOpen &&
        ReactDOM.createPortal(
          <styles.ProfileMenuList ref={menuRef} role="menu" aria-label="Profiles" style={position ?? undefined} onKeyDown={onMenuKeyDown}>
            <styles.ProfileMenuLabel>Write as</styles.ProfileMenuLabel>
            {profiles.map(profile => {
              const isActive = profile.id === activeId;
              const detail = profileDetail(profile, profiles);
              return (
                <styles.ProfileMenuItem
                  key={profile.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  onClick={() => {
                    close(true);
                    if (!isActive) onPick(profile.id);
                  }}
                >
                  <ProfileLogo profile={profile} size={28} />
                  <styles.ProfileMenuText>
                    <strong>{profileLabel(profile)}</strong>
                    {!!detail && <small>{detail}</small>}
                  </styles.ProfileMenuText>
                  {isActive && <styles.ProfileMenuCheck />}
                </styles.ProfileMenuItem>
              );
            })}
          </styles.ProfileMenuList>,
          document.body
        )}
    </styles.ProfileTriggerWrap>
  );
};
